import {
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnInit,
} from '@angular/core';
import { Cms } from '@impact/data';
import { ImpactOverlayRef, OverlayService } from '@impactdk/ngx-overlay';
import { GlobalVehicleDataService } from '../../../core/global-vehicle-data.service';
import { ENVIRONMENT, Environment } from '../../../environment/environment';
import { FeatureDetectionService } from '../../../utils/helpers/feature-detection.service';
import { resolveMediaUrl } from '../../../utils/helpers/media-helpers';
import {
    ExteriorInteriorGallerySpotSlider,
    ExteriorInteriorGallerySpotSliderComponent,
} from '../exterior-interior-gallery-spot-slider.component';
import { carImageFallbackIcon } from './../../../../icons';

@Component({
    selector: 'app-exterior-interior-gallery',
    template: `
        <section class="exterior-interior-gallery-spot">
            <app-exterior-interior-gallery-spot-buttons
                [isExterior]="isExterior"
                (exteriorClick)="selectExteriorGallery()"
                (interiorClick)="selectInteriorGallery()"
            >
            </app-exterior-interior-gallery-spot-buttons>
            <div class="exterior-interior-gallery-spot-image">
                <div class="exterior-interior-gallery-spot-image__column exterior-interior-gallery-spot-image__img-wrapper"
                    (click)="openSlider(0)">
                    <img
                        *ngIf="activeGallery[0]; else fallbackIcon"
                        class="exterior-interior-gallery-spot-image__img"
                        [attr.src]="getMainImgUrl(activeGallery[0])"
                        loading="lazy"
                    />
                </div>
                <div class="exterior-interior-gallery-spot-image__column exterior-interior-gallery-spot-image__grid">
                    <div class="exterior-interior-gallery-spot-image__img-wrapper" (click)="openSlider(1)">
                        <img *ngIf="activeGallery[1]; else fallbackIcon"
                            class="exterior-interior-gallery-spot-image__img"
                            [attr.src]="getTileUrl(activeGallery[1])"
                            loading="lazy" />
                    </div>
                    <div class="exterior-interior-gallery-spot-image__img-wrapper" (click)="openSlider(2)">
                        <img *ngIf="activeGallery[2]; else fallbackIcon"
                            class="exterior-interior-gallery-spot-image__img"
                            [attr.src]="getTileUrl(activeGallery[2])"
                            loading="lazy" />
                    </div>
                    <div class="exterior-interior-gallery-spot-image__img-wrapper" (click)="openSlider(3)">
                        <img *ngIf="activeGallery[3]; else fallbackIcon"
                            class="exterior-interior-gallery-spot-image__img"
                            [attr.src]="getTileUrl(activeGallery[3])"
                            loading="lazy" />
                    </div>
                    <div class="exterior-interior-gallery-spot-image__img-wrapper" (click)="openSlider(4)">
                        <img *ngIf="activeGallery[4]; else fallbackIcon"
                            class="exterior-interior-gallery-spot-image__img"
                            [attr.src]="getTileUrl(activeGallery[4])"
                            loading="lazy" />
                    </div>
                </div>
                <button type="button" class="exterior-interior-gallery-spot-image__text" (click)="openSlider(0)">
                    <div class="exterior-interior-gallery-spot__counter" *ngIf="activeGallery.length">
                        {{ activeGallery.length }} {{ 'generic.pictures' | translate }}
                    </div>
                    <app-rich-text [html]="text"></app-rich-text>
                    <div class="button--primary button--themed" *ngIf="activeGallery.length">{{ buttonText }}</div>
                </button>
            </div>
        </section>

        <ng-template #fallbackIcon>
            <i class="exterior-interior-gallery-spot-image__img exterior-interior-gallery-spot-image__icon"
                [innerHTML]="carImageFallbackIcon | safe: 'html'">
            </i>
        </ng-template>

    `
})
export class ExteriorInteriorGalleryComponent implements OnInit {
    @Input() vehicleModelId?: string | undefined;
    @Input() text?: string;
    @Input() buttonText?: string;

    isExterior: boolean;
    exteriorGallery: Cms.Media[];
    interiorGallery: Cms.Media[];
    activeGallery: Cms.Media[] = [];
    overlay: ImpactOverlayRef;
    carImageFallbackIcon = carImageFallbackIcon;

    constructor(
        @Inject(ENVIRONMENT) private environment: Environment,
        private overlayService: OverlayService,
        private featureDetectionService: FeatureDetectionService,
        private globalVehicleDataService: GlobalVehicleDataService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        if (!this.vehicleModelId) {
            console.warn("[ExteriorInteriorGalleryComponent] No vehicle model ID provided.")
            return;
        }

        this.globalVehicleDataService.getVehicleModelAndVariants(this.vehicleModelId)
            .subscribe((modelAndVariants) => {
                this.exteriorGallery = modelAndVariants.exteriorImages;
                this.interiorGallery = modelAndVariants.interiorImages;
                this.selectExteriorGallery();
            });
    }

    selectExteriorGallery() {
        this.isExterior = true;
        this.activeGallery = this.exteriorGallery ?? [];
        this.cdr.markForCheck();
    }

    selectInteriorGallery() {
        this.isExterior = false;
        this.activeGallery = this.interiorGallery ?? [];
        this.cdr.markForCheck();
    }

    openSlider(index: number) {
        this.overlay = this.overlayService.open(ExteriorInteriorGallerySpotSliderComponent, {
            hasBackdrop: true,
            backdropClass: 'media-slider__overlay-backdrop',
            positionVertical: {
                placement: this.featureDetectionService.isTouchDevice() ? 'center' : 'top',
            },
            data: {
                isExterior: this.isExterior,
                exteriorGallery: this.exteriorGallery,
                interiorGallery: this.interiorGallery,
                startIndex: index
            } as ExteriorInteriorGallerySpotSlider
        });
    }

    getTileUrl(image: Cms.Media) {
        return resolveMediaUrl(image.src, this.environment, {
            format: 'webp',
            rmode: 'crop',
            rxy: image.focalPoint.left + ',' + image.focalPoint.top,
            height: 381,
            quality: 90,
            width: 381,
        })
    }

    getMainImgUrl(image: Cms.Media) {
        return resolveMediaUrl(image.src, this.environment, {
            format: 'webp',
            rmode: 'crop',
            rxy: image.focalPoint.left + ',' + image.focalPoint.top,
            height: 762,
            quality: 90,
            width: 762,
        })
    }
}
